import { css } from '@emotion/react';
import {
  Banner,
  Button,
  Card,
  color,
  Icon,
  iconSize,
  spacing,
  Typography
} from '@uniquegood/realworld-studio-design';
import { RowFlex, JustifySpaceBetween } from '@/styles';

interface PlayTimeData {
  userId: string;
  userName?: string;
  userAvatar?: string;
  startedAt?: string;
  completedAt?: string;
  playTime: number | null;
}

interface PlayTimeLimitSectionProps {
  onUpgradeClick?: () => void;
}

const MOCK_DATA: PlayTimeData[] = [
  {
    userId: 'user_001',
    userName: '{유저 닉네임}',
    userAvatar: 'https://i.pravatar.cc/150?img=1',
    startedAt: '2024-11-24T09:15:30',
    completedAt: undefined,
    playTime: null
  },
  {
    userId: 'user_002',
    userName: '{유저 닉네임}',
    userAvatar: 'https://i.pravatar.cc/150?img=2',
    startedAt: '2024-11-24T10:30:15',
    completedAt: '2024-11-24T11:05:45',
    playTime: 35.5
  },
  {
    userId: 'user_003',
    userName: '{유저 닉네임}',
    userAvatar: 'https://i.pravatar.cc/150?img=3',
    startedAt: '2024-11-24T11:20:00',
    completedAt: '2024-11-24T13:45:18',
    playTime: 145.3
  },
  {
    userId: 'user_004',
    userName: '{유저 닉네임}',
    userAvatar: 'https://i.pravatar.cc/150?img=4',
    startedAt: '2024-11-24T13:00:22',
    completedAt: '2024-11-24T13:42:10',
    playTime: 41.8
  },
  {
    userId: 'user_005',
    userName: '{유저 닉네임}',
    userAvatar: 'https://i.pravatar.cc/150?img=5',
    startedAt: '2024-11-24T14:15:30',
    completedAt: '2024-11-24T15:45:32',
    playTime: 75.53
  }
];

export default function PlayTimeLimitSection({ onUpgradeClick }: PlayTimeLimitSectionProps) {
  const formatDateTime = (dateString?: string) => {
    if (!dateString) return 'TT:TT:TT';
    return new Date(dateString).toLocaleString('ko-KR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  const formatPlayTime = (minutes: number | null) => {
    if (minutes === null) return '-';

    const hours = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    const secs = Math.floor((minutes * 60) % 60);

    if (hours > 0) {
      return `${hours}시간 ${mins}분 ${secs}초`;
    }
    if (mins > 0) {
      return `${mins}분 ${secs}초`;
    }
    return `${secs}초`;
  };

  return (
    <Card cssStyle={CardStyle}>
      {/* 헤더 */}
      <div css={[RowFlex, JustifySpaceBetween, HeaderStyle]}>
        <Typography as="h3" type="heading">
          플레이 시간
        </Typography>
        <Button icon="download_solid" type="plain" disabled>
          다운로드
        </Button>
      </div>

      {/* 배너 */}
      <Banner
        type="informational"
        header={<Typography type="heading">현재 요금제에서 플레이 시간을 볼 수 없어요.</Typography>}
      >
        <>
          <Typography type="body">
            데이터를 확인하려면 엔터프라이즈 또는 클래스 요금제로 업그레이드해 주세요.
          </Typography>
          <Button
            type="primary"
            size="medium"
            onClick={onUpgradeClick}
            cssStyle={UpgradeButtonStyle}
          >
            요금제 업그레이드
          </Button>
        </>
      </Banner>

      {/* 블러 처리된 테이블 */}
      <div css={BlurredTableWrapperStyle}>
        <div css={TableContainerStyle}>
          <table css={TableStyle}>
            <thead>
              <tr>
                <th css={ProfileColumnStyle}>프로필</th>
                <th>유저 닉네임</th>
                <th>시작 시각</th>
                <th>종료 시각</th>
                <th css={PlayTimeColumnStyle}>플레이 시간</th>
              </tr>
            </thead>
            <tbody>
              {MOCK_DATA.map((user) => (
                <tr key={user.userId}>
                  <td css={ProfileCellStyle}>
                    <div css={AvatarStyle}>
                      {user.userAvatar ? (
                        <img src={user.userAvatar} alt={user.userName || 'User'} />
                      ) : (
                        <Icon icon="user_solid" size={iconSize.medium} color={color.icon_subdued} />
                      )}
                    </div>
                  </td>
                  <td>
                    <Typography type="body">{user.userName}</Typography>
                  </td>
                  <td>
                    <Typography type="body">{formatDateTime(user.startedAt)}</Typography>
                  </td>
                  <td>
                    <Typography type="body">{formatDateTime(user.completedAt)}</Typography>
                  </td>
                  <td>
                    <Typography type="body">{formatPlayTime(user.playTime)}</Typography>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Card>
  );
}

const CardStyle = css`
  max-width: 1300px;
  min-width: 740px;
  margin-bottom: ${spacing.margin.xlarge4};
`;

const HeaderStyle = css`
  margin-bottom: ${spacing.margin.xlarge2};
  align-items: center;
`;

const TableContainerStyle = css`
  border: 1px solid ${color.border_default_subdued};
  border-radius: ${spacing.borderRadius.large};
  overflow: hidden;
`;

const TableStyle = css`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: ${spacing.padding.medium};
    text-align: left;
    border-bottom: 1px solid ${color.border_default_subdued};
  }

  th {
    background-color: ${color.surface_default_subdued};
    color: ${color.text_default};
    font-weight: 500;
    font-size: 14px;
  }

  tbody tr:hover {
    background-color: ${color.surface_default_hovered};
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
`;

const ProfileColumnStyle = css`
  width: 69px;
`;

const PlayTimeColumnStyle = css`
  width: 140px;
`;

const ProfileCellStyle = css`
  padding: ${spacing.padding.small} ${spacing.padding.medium} !important;
`;

const AvatarStyle = css`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  background-color: ${color.surface_default_subdued};
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const UpgradeButtonStyle = css`
  margin-top: ${spacing.margin.medium};
  align-self: flex-start;
  background: linear-gradient(90deg, #7800ff 0%, #829bff 100%);
  border: none;
  border-radius: ${spacing.borderRadius.medium};
  padding: ${spacing.padding.small} ${spacing.padding.medium};
  color: ${color.white};
  font-weight: 500;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`;

const BlurredTableWrapperStyle = css`
  margin-top: ${spacing.margin.xlarge2};
  filter: blur(10px);
  pointer-events: none;
  user-select: none;
`;
